'use client'

import * as React from 'react'
import { cn } from '@/lib/css-utils'

interface RatingInputProps {
  value?: number
  onChange?: (value: number) => void
  count?: number
  className?: string
  disabled?: boolean
}

export const RatingInput = React.forwardRef<HTMLDivElement, RatingInputProps>(
  ({ value = 0, onChange, count = 5, className, disabled = false }, ref) => {
    const [hoverValue, setHoverValue] = React.useState<number>(0)

    const handleClick = (index: number) => {
      if (disabled) return
      // If clicking the same dot, clear the selection
      const newValue = value === index + 1 ? 0 : index + 1
      onChange?.(newValue)
    }

    const handleMouseEnter = (index: number) => {
      if (disabled) return
      setHoverValue(index + 1)
    }

    const handleMouseLeave = () => {
      if (disabled) return
      setHoverValue(0)
    }

    return (
      <div
        ref={ref}
        className={cn(
          'flex gap-2 items-center',
          disabled && 'cursor-not-allowed',
          className,
        )}
        onMouseLeave={handleMouseLeave}
      >
        {Array.from({ length: count }).map((_, index) => (
          <button
            key={index}
            type="button"
            onClick={() => handleClick(index)}
            onMouseEnter={() => handleMouseEnter(index)}
            disabled={disabled}
            className={cn(
              'w-4 h-4 sm:w-3 sm:h-3 rounded-full transition-colors',
              'focus:outline-hidden focus:ring-0',
              'active:ring-2 active:ring-black-10 active:ring-offset-2',
              // animation for ring
              'transition-all duration-200 ease-in-out',
              index < value || index < hoverValue
                ? 'bg-ratingInput-focus-bg'
                : 'bg-ratingInput-bg',
              disabled
                ? 'cursor-not-allowed'
                : 'cursor-pointer hover:bg-ratingInput-hover-bg',
            )}
            aria-label={`Rate ${index + 1} out of ${count}`}
          />
        ))}
      </div>
    )
  },
)

RatingInput.displayName = 'RatingInput'
